import { useState, useEffect } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import AppIcon from '@/components/content/app/AppIcon'
import Marquee from '@/components/ui/Marquee'
import { Icon } from '@/icons/Icon'
import { easeMedium } from '@/lib/transitions'
import { cn, getBrandColors } from '@/lib/utils'
import useMediaQuery from '@/hooks/useMediaQuery'
import type { CategoryType, AppIconType } from '@/app/(app)/fetchers'
import type { App, AppCategoryRecommendation } from '@/payload-types'

type CategoriesTypeProps = {
  categories: CategoryType[]
}

type CategoryCardProps = {
  category: CategoryType
  selectedCategoryId: number | undefined
  setSelectedCategory: (category: CategoryType) => void
}

export default function CategoriesType({ categories }: CategoriesTypeProps) {
  const filteredCategories = categories.filter(
    (cat) => (cat.appRecommendations?.docs?.length ?? 0) >= 5,
  )

  const visibleCategories = filteredCategories.length > 0 ? filteredCategories : categories
  const defaultCategory =
    visibleCategories.find((category) => category.name === 'Project Management') ??
    visibleCategories[0]

  const [selectedCategory, setSelectedCategory] = useState<CategoryType | undefined>(
    defaultCategory,
  )
  const [apps, setApps] = useState<AppIconType[]>([])

  const halfLength = Math.ceil(visibleCategories.length / 2)
  const firstHalf = visibleCategories.slice(0, halfLength)
  const secondHalf = visibleCategories.slice(halfLength)

  useEffect(() => {
    if (selectedCategory) {
      const appRecs = selectedCategory.appRecommendations?.docs as AppCategoryRecommendation[]
      const validApps =
        appRecs?.slice(0, 7).map((rec) => {
          const app = rec.app as App
          return {
            name: app.name,
            slug: app.slug,
            icon: app.icon,
            glyph: app.glyph,
            brandBackgroundColor: app.brandBackgroundColor,
            brandPrimaryColor: app.brandPrimaryColor,
            brandSecondaryColor: app.brandSecondaryColor,
            brandTertiaryColor: app.brandTertiaryColor,
          }
        }) || []

      setApps(validApps)
    }
  }, [selectedCategory])

  return (
    <div className="mt-auto h-[280px] w-full">
      <div className="flex h-[calc(100%-88px)] w-full flex-col justify-center gap-1 overflow-hidden transition-[height] duration-500 md:h-[calc(100%-138px)]">
        <Marquee duration={120} pauseOnHover={true} className="py-1">
          <div className="flex w-fit gap-3 pl-3">
            {firstHalf.map((category, index) => (
              <CategoryCard
                key={index}
                category={category}
                selectedCategoryId={selectedCategory?.id}
                setSelectedCategory={setSelectedCategory}
              />
            ))}
          </div>
        </Marquee>

        <Marquee duration={120} pauseOnHover={true} className="py-1">
          <div className="flex w-fit gap-3 pl-3">
            {secondHalf.map((category, index) => (
              <CategoryCard
                key={index}
                category={category}
                selectedCategoryId={selectedCategory?.id}
                setSelectedCategory={setSelectedCategory}
              />
            ))}
          </div>
        </Marquee>
      </div>

      <AnimatePresence>
        {selectedCategory && (
          <CategoryAppDock
            key={selectedCategory.id}
            selectedCategory={selectedCategory.name}
            apps={apps}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

export function CategoryCard({
  category,
  selectedCategoryId,
  setSelectedCategory,
}: CategoryCardProps) {
  return (
    <div
      onClick={() => setSelectedCategory(category)}
      className={cn(
        'group flex h-10 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-black/10 bg-white pl-1 pr-5 transition-all hover:border-primary-200 hover:shadow-[0px_0px_0px_3px_rgba(255,240,242,1.00)]',
        selectedCategoryId === category.id &&
          'border-primary-200 shadow-[0px_0px_0px_3px_rgba(255,240,242,1.00)]',
      )}
    >
      <div
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors group-hover:bg-primary-50 group-hover:text-primary-500',
          selectedCategoryId === category.id && 'bg-primary-50 text-primary-500',
        )}
      >
        <Icon name={category.icon ?? ''} />
      </div>
      <span
        className={cn(
          'shrink-0 text-13 font-medium text-gray-500 transition-colors group-hover:text-primary-500',
          selectedCategoryId === category.id && 'text-primary-500',
        )}
      >
        {category.name}
      </span>
    </div>
  )
}

export function CategoryAppDock({
  selectedCategory,
  apps,
}: {
  selectedCategory: string
  apps: AppIconType[]
}) {
  const { isMobile, isSm } = useMediaQuery()

  return (
    <motion.div
      key={selectedCategory}
      initial={{ opacity: 0, y: '100%', x: '-50%' }}
      animate={{ opacity: 1, y: '0%', x: '-50%' }}
      exit={{ opacity: 0, y: '100%', x: '-50%' }}
      transition={easeMedium}
      className="absolute bottom-0 left-1/2 flex w-fit items-center gap-2.5 overflow-hidden rounded-t-2xl border-x border-t border-black/10 bg-gradient-to-t from-gray-100 to-white px-4 py-3 md:gap-5 md:rounded-t-3xl md:px-8 md:py-6"
    >
      {apps.map((app) => {
        const { backgroundColor } = getBrandColors(app)

        return (
          <AppIcon
            key={app.name}
            icon={app.icon}
            glyph={app.glyph}
            name={app.name}
            slug={app.slug}
            background={backgroundColor}
            size={isMobile || isSm ? 40 : 64}
            className="shadow-subtle"
          />
        )
      })}
    </motion.div>
  )
}
